import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../../../api/apiClient'
import { ROUTES } from '../../../../constants/routes'
import { useAuthContext } from '../../../../state/useAuth'
import { setAuthorizationHeader } from '../utils'

type TRetryConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
}

const isUnauthorized = ({ response }: AxiosError) => response?.status === 401

const useResponseErrorInterceptor = () => {
  const navigate = useNavigate()
  const { removeAuthData, refreshAccessToken } = useAuthContext()

  return useCallback(
    async (error: AxiosError) => {
      if (!axios.isAxiosError(error) || !isUnauthorized(error)) {
        return Promise.reject(error)
      }

      const requestConfig = error.config as TRetryConfig | undefined

      if (!requestConfig || requestConfig._retry) {
        await removeAuthData()
        navigate(ROUTES.LANDING_PAGE, { replace: true })
        return Promise.reject(error)
      }

      requestConfig._retry = true

      try {
        const token = await refreshAccessToken(0)
        if (!token) {
          throw new Error('No valid access token')
        }

        setAuthorizationHeader(requestConfig, token)
        return await apiClient.request(requestConfig)
      } catch (refreshError) {
        await removeAuthData()
        navigate(ROUTES.LANDING_PAGE, { replace: true })
        return Promise.reject(refreshError)
      }
    },
    [navigate, refreshAccessToken, removeAuthData]
  )
}

export const useOtherErrorsHandling = () => {
  const navigate = useNavigate()
  return useCallback(async ({ response, code }: AxiosError) => {
    if (code === 'ERR_CANCELED') return

    if (!response) {
      navigate(ROUTES.MAINTENANCE)
      return
    }

    const statusUrl = response?.config?.url === '/parse/status'

    if (response.status > 401 && !statusUrl) {
      navigate(ROUTES.MAINTENANCE)
    }
  }, [navigate])
}

export const useResponseInterceptor = () => {
  const errorInterceptor = useResponseErrorInterceptor()
  const otherErrorsHandling = useOtherErrorsHandling()

  useEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        await otherErrorsHandling(error)
        return await errorInterceptor(error)
      }
    )

    return () => {
      apiClient.interceptors.response.eject(interceptor)
    }
  }, [errorInterceptor, otherErrorsHandling])
}
